/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import { DataGrid } from '../../../components/common/datagrid/ResponsiveDataGrid';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FactoryIcon from '@mui/icons-material/Factory';
import BusinessIcon from '@mui/icons-material/Business';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ProductionStartDialog from './ProductionStartDialog';
import ProductionDetailDialog from './ProductionDetailDialog';
import ProcessDetailDialog from './ProcessDetailDialog';
import OutsourceDialog from './OutsourceDialog';
import TextField from '@mui/material/TextField';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import * as manufacturingApi from '../../../lib/api/manufacturing';
import { uploadProcessFile } from '../../../lib/api/file';
import { syncProductionProcesses, toDateOnly } from '../../../utils/manufacturing/routingSync';

// 상태 설정
const STATUS_CONFIG = {
  PENDING: { label: '대기', chipColor: 'default' },
  IN_PROGRESS: { label: '진행중', chipColor: 'warning' },
  COMPLETED: { label: '완료', chipColor: 'success' },
  SKIPPED: { label: '건너뜀', chipColor: 'default' },
};

const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;

const hasAuth = (user, authTyp) =>
  (user?.privilegeList || []).some(auth => auth?.authTyp === authTyp && auth?.authYn === 'Y');

const sameUuid = (left, right) =>
  !!left && !!right && String(left).toLowerCase() === String(right).toLowerCase();

const isShipmentPhase = (detail) => String(detail?.phaseRole || '').toUpperCase() === 'SHIPMENT';

/**
 * 생산 탭 컨테이너 (통합 버전)
 * - 공정 순서 + 생산 실적을 하나의 테이블로 통합
 * - 현재 진행중인 공정 행 하이라이트
 * - 공정 시작/완료 액션을 테이블 행에서 직접 수행
 * - 행 클릭 시 생산 실적 상세 확인
 */
const ProductionTabContainer = ({ productionPlan, planId, planData, onRoutingUpdate }) => {
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));
  const canProcessShipment = hasAuth(user, 'MANU_SHIPMENT_PROCESS');
  const canUpdatePhaseAssignment = hasAuth(user, 'MANU_PHASE_ASSIGN_UPDATE');
  const canUpdateClosedPhaseAssignment = hasAuth(user, 'MANU_PHASE_ASSIGN_CLOSED_UPDATE');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [routingData, setRoutingData] = useState(null);
  const [productionRows, setProductionRows] = useState([]);
  const [designNumber, setDesignNumber] = useState('-');
  
  // 다이얼로그 상태
  const [openStartDialog, setOpenStartDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [openProcessDialog, setOpenProcessDialog] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', data: null });
  const [actionLoading, setActionLoading] = useState(null);
  const [openOutsourceDialog, setOpenOutsourceDialog] = useState(false);
  const [selectedOutsourceProcess, setSelectedOutsourceProcess] = useState(null);
  
  // 공정 완료 다이얼로그 상태
  const [completeFiles, setCompleteFiles] = useState([]);
  const [completeRemarks, setCompleteRemarks] = useState('');
  // 공정검사(선택) - 체크했을 때만 생산수량/불량수량을 받는다.
  // 불량수량 0이 "검사해서 불량 없음"인지 "검사 안 함"인지 구분하기 위한 체크박스다.
  const [completeQcYn, setCompleteQcYn] = useState(false);
  const [completeOutputQty, setCompleteOutputQty] = useState(0);
  const [completeDefectQty, setCompleteDefectQty] = useState(0);
  // 작업 내용을 서버에서 만들어 오는 동안. 완료를 막지는 않는다.
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);

  // 검사 공정(완품 검사) 판정 다이얼로그 상태
  const [qcJudgeDialog, setQcJudgeDialog] = useState({ open: false, data: null });
  const [qcJudgeRemarks, setQcJudgeRemarks] = useState('');
  const [qcJudgeLoading, setQcJudgeLoading] = useState(null); // 'PASS' | 'FAIL' | null

  // ==========================================
  // 데이터 로드
  // ==========================================
  const fetchRoutingData = useCallback(async () => {
    if (!planId) return;
    try {
      const response = await manufacturingApi.getRoutingsByPlan(planId);
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        setRoutingData(response.data.data[0]);
      } else {
        setRoutingData(null);
      }
    } catch (err) {
      console.error('라우팅 데이터 조회 실패:', err);
      setRoutingData(null);
    }
  }, [planId]);

  const fetchProductionData = useCallback(async () => {
    if (!planId) return;
    try {
      const response = await manufacturingApi.getProductionsByPlan(planId);
      if (response.data.success && response.data.data) {
        const productions = response.data.data.content || response.data.data || [];
        const formattedProductions = productions.map((item, index) => {
          const rowId = item.resultId || item.productionId || item.id || `${item.planId}-${index}`;
          return {
            id: rowId,
            productionId: rowId,
            resultId: rowId,
            planId: item.planId,
            manuNo: item.productionNumber || item.manuNo || item.planNumber || '-',
            processName: item.processName || '',
            designManuNo: item.designNumber || item.designManuNo || '-',
            type: item.productionType || item.type || 'INTERNAL',
            typeLabel: (item.productionType || item.type) === 'EXTERNAL' ? '외주' : '내부',
            status: item.status,
            statusLabel: { 'PENDING': '대기', 'IN_PROGRESS': '생산중', 'COMPLETED': '생산완료', 'CANCELLED': '취소' }[item.status] || item.status || '대기',
            managerName: item.managerName || item.workerName || '-',
            worker: item.managerName || item.workerName || '-',
            productName: item.productName || '',
            targetQuantity: item.targetQuantity || 0,
            producedQuantity: item.producedQuantity || 0,
            remarks: item.remarks || '-',
            startDate: item.startDate || item.workDate || '-',
            endDate: item.endDate || '-',
            createdAt: item.createdAt ? item.createdAt.split(' ')[0] : '-',
            products: item.products || [],
          };
        });
        setProductionRows(formattedProductions);
      } else {
        setProductionRows([]);
      }
    } catch (err) {
      console.error('생산 목록 조회 에러:', err);
      setProductionRows([]);
    }
  }, [planId]);

  const fetchDesignNumber = useCallback(async () => {
    if (!planId) return;
    try {
      const response = await manufacturingApi.getDesignsByPlan(planId);
      if (response.data.success && response.data.data) {
        const designs = response.data.data.content || response.data.data || [];
        // APPROVED/COMPLETED 상태의 최신 설계번호 사용
        const approvedDesign = designs.find(d => d.status === 'APPROVED' || d.status === 'COMPLETED');
        const latestDesign = approvedDesign || designs[0];
        if (latestDesign?.designNumber) {
          setDesignNumber(latestDesign.designNumber);
        }
      }
    } catch (err) {
      console.error('설계번호 조회 실패:', err);
    }
  }, [planId]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchRoutingData(), fetchProductionData(), fetchDesignNumber()]);
    } catch (err) {
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [fetchRoutingData, fetchProductionData, fetchDesignNumber]);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  // ==========================================
  // 공정 상태 변경 핸들러
  // ==========================================
  const canProcessDetailAction = useCallback((detail) => {
    if (!isShipmentPhase(detail)) return true;
    return sameUuid(detail?.workerUuid, user?.usrUuid) || canProcessShipment;
  }, [canProcessShipment, user?.usrUuid]);

  const handleStartProcess = useCallback(async (routingDetUuid, processNm) => {
    setActionLoading(routingDetUuid);
    try {
      const response = await manufacturingApi.updateRoutingProcessStatus(routingDetUuid, { processStatus: 'IN_PROGRESS' });
      if (response.data.success) {
        await Promise.all([fetchRoutingData(), fetchProductionData()]);
        if (onRoutingUpdate) onRoutingUpdate();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || '공정을 시작할 수 없습니다.';
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setActionLoading(null);
    }
  }, [fetchRoutingData, fetchProductionData, onRoutingUpdate]);

  const handleCompleteProcess = useCallback(async (routingDetUuid, processNm) => {
    // 공정검사를 실시했다면 불량수량이 생산수량을 넘을 수 없다.
    // 넘으면 숫자가 말이 안 되므로(불량이 생산량보다 많음) 저장을 막는다.
    if (completeQcYn) {
      const outputQty = Number(completeOutputQty) || 0;
      const defectQty = Number(completeDefectQty) || 0;
      if (defectQty > outputQty) {
        setError('불량수량은 생산수량을 넘을 수 없습니다.');
        setTimeout(() => setError(null), 5000);
        return;
      }
    }

    setActionLoading(routingDetUuid);
    setUploadProgress(true);
    try {
      // 1. 첨부파일 업로드
      for (const file of completeFiles) {
        try {
          await uploadProcessFile(file, planId, routingDetUuid, processNm, completeRemarks || '작업완료');
        } catch (uploadErr) {
          console.error('파일 업로드 실패:', uploadErr);
        }
      }

      // 2. 공정 상태 변경
      // qcYn/outputQty/defectQty: 공정검사를 실시한 경우에만 값을 싣는다.
      // 체크 안 하면 전부 0/N으로 보내 "검사 안 함"과 "검사해서 불량 0"을 구분한다.
      const response = await manufacturingApi.updateRoutingProcessStatus(routingDetUuid, {
        processStatus: 'COMPLETED',
        remarks: completeRemarks || '작업완료',
        qcYn: completeQcYn ? 'Y' : 'N',
        outputQty: completeQcYn ? (Number(completeOutputQty) || 0) : 0,
        defectQty: completeQcYn ? (Number(completeDefectQty) || 0) : 0,
      });
      if (response.data.success) {
        await Promise.all([fetchRoutingData(), fetchProductionData()]);
        if (onRoutingUpdate) onRoutingUpdate();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || '공정을 완료할 수 없습니다.';
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setActionLoading(null);
      setUploadProgress(false);
      setConfirmDialog({ open: false, type: '', data: null });
      setCompleteFiles([]);
      setCompleteRemarks('');
      setCompleteQcYn(false);
      setCompleteOutputQty(0);
      setCompleteDefectQty(0);
    }
  }, [fetchRoutingData, fetchProductionData, onRoutingUpdate, completeFiles, completeRemarks, completeQcYn, completeOutputQty, completeDefectQty, planId]);

  const openConfirmDialog = useCallback((type, detail) => {
    if ((type === 'start' || type === 'complete') && !canProcessDetailAction(detail)) {
      setError('출고 phase 처리 권한이 없습니다.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    if (type === 'complete') {
      // 우선 사실만으로 한 줄을 채워둔다. 서버가 다듬어 주면 그걸로 바꾼다.
      // "작업완료" 만 남으면 나중에 무슨 작업이었는지 알 수 없다.
      setCompleteRemarks('작업완료');
      setCompleteFiles([]);
      // 검사 대상 여부는 워크플로우 설계값(qcTargetYn) 그대로 따른다. 예전처럼 사용자가
      // 체크박스로 끄고 켤 수 있는 값이 아니다 - 대상 공정이면 항상 실측값을 받는다.
      setCompleteQcYn(detail?.qcTargetYn === 'Y');
      setCompleteDefectQty(0);
      // 생산수량 기본값은 생산계획 수량으로 미리 채운다. 공정마다 다시 입력하게 하면 아무도 안 쓴다.
      setCompleteOutputQty(planData?.planQty || planData?.planQuantity || planData?.targetQuantity || planData?.quantity || 0);
      setSummaryLoading(true);

      manufacturingApi.getProcessCompletionSummary(detail?.routingDetUuid, planId)
        .then((res) => {
          const text = res?.data?.data;
          if (text) setCompleteRemarks(text);
        })
        // 실패해도 넘어간다. 작업 내용을 못 만들었다고 공정 완료가 막히면 안 된다.
        .catch(() => {})
        .finally(() => setSummaryLoading(false));
    }
    setConfirmDialog({ open: true, type, data: detail });
  }, [canProcessDetailAction, planId, planData]);

  // ==========================================
  // 완품 검사(INSPECTION 공정) 판정
  // ==========================================
  const openQcJudgeDialog = useCallback((detail) => {
    if (!canProcessDetailAction(detail)) {
      setError('출고 phase 처리 권한이 없습니다.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    setQcJudgeRemarks('');
    setQcJudgeDialog({ open: true, data: detail });
  }, [canProcessDetailAction]);

  const closeQcJudgeDialog = useCallback(() => {
    setQcJudgeDialog({ open: false, data: null });
    setQcJudgeRemarks('');
  }, []);

  const handleQcJudge = useCallback(async (result) => {
    const detail = qcJudgeDialog.data;
    if (!detail) return;
    setQcJudgeLoading(result);
    try {
      const response = await manufacturingApi.judgeRoutingFinalQc(detail.routingDetUuid, {
        result,
        remarks: qcJudgeRemarks,
      });
      if (response.data.success) {
        await Promise.all([fetchRoutingData(), fetchProductionData()]);
        if (onRoutingUpdate) onRoutingUpdate();
        closeQcJudgeDialog();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'QC 판정 처리에 실패했습니다.';
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setQcJudgeLoading(null);
    }
  }, [qcJudgeDialog, qcJudgeRemarks, fetchRoutingData, fetchProductionData, onRoutingUpdate, closeQcJudgeDialog]);

  const handleConfirmAction = useCallback(() => {
    const { type, data } = confirmDialog;
    if ((type === 'start' || type === 'complete') && !canProcessDetailAction(data)) {
      setError('출고 phase 처리 권한이 없습니다.');
      setTimeout(() => setError(null), 5000);
      setConfirmDialog({ open: false, type: '', data: null });
      return;
    }
    if (type === 'start') handleStartProcess(data.routingDetUuid, data.processNm);
    else if (type === 'complete') handleCompleteProcess(data.routingDetUuid, data.processNm);
    setConfirmDialog({ open: false, type: '', data: null });
  }, [confirmDialog, handleStartProcess, handleCompleteProcess, canProcessDetailAction]);

  // ==========================================
  // 생산 실적 핸들러
  // ==========================================
  const handleRowClick = useCallback((param) => {
    // 공정 상세 다이얼로그 열기 (모든 공정)
    setSelectedProcess(param.row);
    setOpenProcessDialog(true);
  }, []);

  const handleUpdateProduction = useCallback(async (updatedData) => {
    try {
      const productionId = updatedData.productionId || updatedData.resultId || updatedData.id;
      const response = await manufacturingApi.updateProduction(productionId, updatedData);
      if (!response.data.success) {
        throw new Error(response.data.message || '생산 수정에 실패했습니다.');
      }
      await fetchProductionData();
      return true;
    } catch (err) {
      console.error('생산 수정 에러:', err);
      alert(err?.response?.data?.message || err.message || '생산 수정에 실패했습니다.');
      return false;
    }
  }, [fetchProductionData]);

  const handleStatusChange = useCallback(async (productionId, newStatus, remarks) => {
    try {
      const response = await manufacturingApi.updateProductionStatus(productionId, { status: newStatus, remarks });
      if (response.data.success) {
        await fetchProductionData();
        if (newStatus === 'COMPLETED') {
          await syncProductionProcesses(planId, 'COMPLETED', onRoutingUpdate);
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('상태 변경 에러:', err);
      return false;
    }
  }, [fetchProductionData, planId, onRoutingUpdate]);

  const handleStartProduction = useCallback(async (formData) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const productId = planData?.productId || planData?.productUuid || null;
      const targetQuantity = planData?.planQty || planData?.planQuantity || planData?.targetQuantity || planData?.quantity || 0;
      
      const productionData = {
        planId, productId, productionDate: today, workDate: today,
        targetQuantity, producedQuantity: 0, defectQuantity: 0,
        workerId: formData.workerUuid, workerName: formData.worker,
        managerId: formData.workerUuid, managerName: formData.worker,
        workHours: 0, status: 'IN_PROGRESS',
        remarks: formData.remarks || `생산종류: ${formData.processTypeLabel || formData.processType}`,
      };
      
      const response = await manufacturingApi.createProduction(productionData);
      if (response.data.success) {
        await fetchProductionData();
        setOpenStartDialog(false);
        await syncProductionProcesses(planId, 'IN_PROGRESS', onRoutingUpdate);
      }
    } catch (err) {
      console.error('생산 생성 에러:', err);
      alert('생산 등록에 실패했습니다.');
    }
  }, [planId, planData, fetchProductionData, onRoutingUpdate]);

  // ==========================================
  // 공정 진행 정보 계산
  // ==========================================
  const progress = useMemo(() => {
    if (!routingData?.details) return { total: 0, completed: 0, inProgress: 0, pending: 0, percent: 0 };
    const details = routingData.details;
    const total = details.length;
    const completed = details.filter(d => d.processStatus === 'COMPLETED' || d.processStatus === 'SKIPPED').length;
    const inProgress = details.filter(d => d.processStatus === 'IN_PROGRESS').length;
    const pending = details.filter(d => d.processStatus === 'PENDING').length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inProgress, pending, percent };
  }, [routingData]);

  const canStartProcess = (detail) => {
    if (!routingData?.details) return false;
    if (detail.processStatus !== 'PENDING') return false;
    const currentSeq = detail.processSeq;
    return !routingData.details.find(
      d => d.processSeq < currentSeq && d.processStatus !== 'COMPLETED' && d.processStatus !== 'SKIPPED'
    );
  };

  const isAllCompleted = progress.total > 0 && progress.completed === progress.total;

  // ==========================================
  // 통합 테이블 데이터: 라우팅(공정) + 생산실적 병합
  // ==========================================
  const mergedRows = useMemo(() => {
    if (!routingData?.details) return [];

    return routingData.details.map(d => {
      // 공정명으로 매칭되는 생산 실적 찾기
      const matchedProduction = productionRows.find(p => {
        const pName = (p.processName || '').trim().toLowerCase();
        const dName = (d.processNm || '').trim().toLowerCase();
        return pName === dName;
      });

      return {
        ...d,
        id: d.routingDetUuid,
        // 생산 실적 정보 병합
        designManuNo: matchedProduction?.designManuNo || designNumber || '-',
        productName: matchedProduction?.productName || planData?.productName || '-',
        productionType: matchedProduction?.typeLabel || '-',
        productionStatus: matchedProduction?.statusLabel || '-',
        productionWorker: matchedProduction?.worker || null,
        targetQuantity: matchedProduction?.targetQuantity || null,
        producedQuantity: matchedProduction?.producedQuantity || null,
        productionCreatedAt: matchedProduction?.createdAt || '-',
        // 원본 생산 실적 데이터 (상세 다이얼로그용)
        _production: matchedProduction || null,
      };
    });
  }, [routingData, productionRows, planData]);

  // ==========================================
  // 통합 테이블 컬럼 정의
  // ==========================================
  const columns = useMemo(() => [
    {
      field: 'processSeq',
      headerName: '순서',
      width: 55,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'processNm',
      headerName: '공정명',
      width: 110,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={params.row.processStatus === 'IN_PROGRESS' ? 700 : 400}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      // 두 QC 는 성격이 다르다. 완품검사는 계획 전체를 합격/불합격으로 판정하고(불합격이면
      // 전량 폐기), 공정검사는 그 공정에서 선택적으로 실시한 부분 검사다(수량 기준).
      // 목록에 공정명과 상태만 있어서 어느 쪽인지 화면으로 구분할 수가 없었다.
      field: 'qcYn',
      headerName: 'QC',
      width: 190,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        const row = params.row || {};
        if (row.processMstTyp === 'INSPECTION') {
          const judged = row.processStatus === 'COMPLETED';
          return (
            <Chip
              label={judged ? '완품검사 판정완료' : '완품검사 대기'}
              size="small"
              color={judged ? 'success' : 'warning'}
              variant="outlined"
            />
          );
        }
        if (row.qcYn === 'Y') {
          const out = Number(row.outputQty) || 0;
          const def = Number(row.defectQty) || 0;
          return (
            <Chip
              label={`공정검사 ${out} / 불량 ${def}`}
              size="small"
              color={def > 0 ? 'warning' : 'info'}
              variant="outlined"
            />
          );
        }
        // 검사 대상으로 지정됐지만(qcTargetYn='Y') 아직 실적이 안 들어온 공정.
        // "검사 없음"과 섞이면 실제로 검사 안 해도 되는 공정과 구분이 안 된다.
        if (row.qcTargetYn === 'Y') {
          return (
            <Chip label="검사 대기" size="small" color="warning" variant="outlined" />
          );
        }
        // 검사 없이 지나간 공정. 빈칸이 아니라 그렇게 적어야 "검사했는데 값이 없다" 와 갈린다.
        return (
          <Typography variant="caption" color="text.secondary">검사 없음</Typography>
        );
      },
    },
    {
      field: 'processStatus',
      headerName: '공정상태',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const config = getStatusConfig(params.value);
        return (
          <Chip
            label={config.label}
            size="small"
            color={config.chipColor}
            variant={params.value === 'IN_PROGRESS' ? 'filled' : 'outlined'}
            sx={{ fontWeight: params.value === 'IN_PROGRESS' ? 600 : 400 }}
          />
        );
      },
    },
    {
      field: 'workerNm',
      headerName: '담당자',
      width: 85,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || params.row.productionWorker || planData?.productionManagerName || '-',
    },
    {
      field: 'designManuNo',
      headerName: '설계번호',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.value || params.value === '-') return '-';
        return (
          <Chip label={params.value} size="small" color="info" variant="outlined" sx={{ fontWeight: 600, maxWidth: 140 }} />
        );
      },
    },
    {
      field: 'stdWorkTime',
      headerName: '표준시간',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value ? `${params.value}h` : '-',
    },
    {
      field: 'startDate',
      headerName: '시작일',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        // actualStartDt/planStartDt 는 datetime 문자열("yyyy-MM-dd HH:mm:ss")이다. 목록
        // 컬럼은 훑어보는 용도라 시각까지 필요 없고, 폭도 좁아(100) 날짜만 자른다.
        if (row.actualStartDt) return toDateOnly(row.actualStartDt);
        if (row.planStartDt) return (
          <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
            ({toDateOnly(row.planStartDt)})
          </Typography>
        );
        return '-';
      },
    },
    {
      field: 'endDate',
      headerName: '완료일',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        if (row.actualEndDt) return toDateOnly(row.actualEndDt);
        return '-';
      },
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        const detail = params.row;
        const isStartable = canStartProcess(detail);
        const isInProgress = detail.processStatus === 'IN_PROGRESS';
        const isCompleted = detail.processStatus === 'COMPLETED' || detail.processStatus === 'SKIPPED';
        const isPending = detail.processStatus === 'PENDING';
        const isLocked = isPending && !isStartable;
        const isLoading = actionLoading === detail.routingDetUuid;
        const hasProduction = !!detail._production;
        const canProcessAction = canProcessDetailAction(detail);

        if (isCompleted) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
              {hasProduction && (
                <Tooltip title="실적 상세보기">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedProduction(detail._production); setOpenDetailDialog(true); }}>
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#999' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        }
        if (isPending && isStartable) {
          return (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <PlayCircleOutlineIcon />}
                onClick={(e) => { e.stopPropagation(); openConfirmDialog('start', detail); }}
                disabled={isLoading || !canProcessAction}
                sx={{ fontSize: '0.7rem', py: 0.2, px: 1 }}
              >
                시작
              </Button>
              <Tooltip title="외주 설정">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setSelectedOutsourceProcess(detail); setOpenOutsourceDialog(true); }}
                  sx={{ color: detail.processType === 'EXTERNAL' ? '#1976d2' : '#9e9e9e' }}
                >
                  <BusinessIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }
        if (isInProgress) {
          const isInspectionProcess = detail.processMstTyp === 'INSPECTION';
          return (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Button
                size="small"
                variant="contained"
                color={isInspectionProcess ? 'warning' : 'success'}
                startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  // 검사 공정(완품 QC)은 합/불 판정 다이얼로그로, 그 외는 기존 완료 다이얼로그로.
                  if (isInspectionProcess) openQcJudgeDialog(detail);
                  else openConfirmDialog('complete', detail);
                }}
                disabled={isLoading || !canProcessAction}
                sx={{ fontSize: '0.7rem', py: 0.2, px: 1 }}
              >
                {isInspectionProcess ? '검사 판정' : '완료'}
              </Button>
              <Tooltip title="외주 설정">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setSelectedOutsourceProcess(detail); setOpenOutsourceDialog(true); }}
                  sx={{ color: detail.processType === 'EXTERNAL' ? '#1976d2' : '#9e9e9e' }}
                >
                  <BusinessIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }
        if (isLocked) {
          return (
            <Tooltip title="이전 공정 완료 필요">
              <LockIcon sx={{ color: '#bdbdbd', fontSize: 20 }} />
            </Tooltip>
          );
        }
        return null;
      },
    },
  ], [actionLoading, routingData, planData, openConfirmDialog, openQcJudgeDialog, canProcessDetailAction]);

  // ==========================================
  // 렌더링
  // ==========================================
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* ========================================== */}
      {/* 공정 진행 현황 (compact) */}
      {/* ========================================== */}
      <Paper elevation={2} sx={{ p: 2, mb: 2, borderLeft: '4px solid #ff9800' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FactoryIcon color="warning" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold">공정 진행 현황</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip label={`완료 ${progress.completed}/${progress.total}`} color="success" size="small" />
            {progress.inProgress > 0 && <Chip label={`진행중 ${progress.inProgress}`} color="warning" size="small" />}
            {progress.pending > 0 && <Chip label={`대기 ${progress.pending}`} color="default" size="small" />}
            <IconButton size="small" onClick={loadAllData} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress.percent}
              sx={{
                height: 10, borderRadius: 5, bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: isAllCompleted ? '#4caf50' : '#ff9800' },
              }}
            />
          </Box>
          <Typography variant="subtitle1" fontWeight="bold" color={isAllCompleted ? 'success.main' : 'warning.main'}>
            {progress.percent}%
          </Typography>
        </Box>

        {isAllCompleted && (
          <Alert severity="success" sx={{ mt: 1.5 }} icon={<CheckCircleIcon />}>
            모든 공정이 완료되었습니다! 완료 탭에서 최종 처리를 진행하세요.
          </Alert>
        )}
      </Paper>

      {/* ========================================== */}
      {/* 통합 공정 테이블 (공정 순서 + 생산 실적) */}
      {/* ========================================== */}
      {mergedRows.length > 0 ? (
        <Paper elevation={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                공정 목록
              </Typography>
              <Chip label={`${mergedRows.length}건`} size="small" color="default" />
            </Box>
            {mergedRows.some(r => r.processStatus !== 'COMPLETED') && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayCircleOutlineIcon />}
                onClick={() => setOpenStartDialog(true)}
              >
                시작
              </Button>
            )}
          </Box>
          <Box sx={{ width: '100%' }}>
            <DataGrid
              rows={mergedRows}
              columns={columns}
              density="compact"
              hideFooter
              disableRowSelectionOnClick
              autoHeight
              onRowClick={handleRowClick}
              getRowClassName={(params) => {
                if (params.row.processStatus === 'IN_PROGRESS') return 'row-in-progress';
                if (params.row.processStatus === 'COMPLETED') return 'row-completed';
                return '';
              }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .row-in-progress': {
                  bgcolor: '#fff3e0',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#ffe0b2' },
                },
                '& .row-completed': {
                  bgcolor: '#f1f8e9',
                  color: '#666',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'default',
                },
                '& .MuiDataGrid-row:has(.has-production)': {
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
        </Paper>
      ) : (
        <Alert severity="info">
          워크플로우가 아직 설정되지 않았습니다. 설계 완료 후 자동 생성되거나, 생산계획 탭에서 수동 설정해주세요.
        </Alert>
      )}

      {/* ========================================== */}
      {/* 다이얼로그 */}
      {/* ========================================== */}

      {/* 공정 시작 확인 다이얼로그 */}
      <Dialog
        open={confirmDialog.open && confirmDialog.type === 'start'}
        onClose={() => setConfirmDialog({ open: false, type: '', data: null })}
        maxWidth="xs" fullWidth
      >
        <DialogTitle>공정 시작 확인</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{confirmDialog.data?.processNm}</strong> 공정을 시작하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', data: null })}>취소</Button>
          <Button onClick={handleConfirmAction} variant="contained" color="primary">시작</Button>
        </DialogActions>
      </Dialog>

      {/* 공정 완료 다이얼로그 (첨부파일 + 비고) */}
      <Dialog
        open={confirmDialog.open && confirmDialog.type === 'complete'}
        onClose={() => setConfirmDialog({ open: false, type: '', data: null })}
        maxWidth="sm" fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <span>공정 완료 - {confirmDialog.data?.processNm}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* 안내 메시지 */}
          <Alert severity="info" sx={{ mb: 2 }}>
            작업 내용은 자동으로 채워집니다. 고쳐 쓰셔도 됩니다.
            <strong> 첨부파일은 선택</strong>입니다.
          </Alert>

          {/* 비고 입력 */}
          <TextField
            label="작업 내용"
            value={completeRemarks}
            onChange={(e) => setCompleteRemarks(e.target.value)}
            multiline
            rows={2}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="작업 내용을 입력해주세요"
            helperText={summaryLoading ? '작업 내용을 정리하는 중입니다...' : ' '}
          />

          {/* 공정검사(대상 공정만 표시)
              - 검사 대상 여부(qcTargetYn)는 워크플로우 설계 단계에서 미리 정해둔 값이다.
                실행 시점에는 체크박스로 껐다 켰다 할 수 없고, 실제 수치만 입력받는다.
              - INSPECTION(완품검사)은 합격/불합격 판정 다이얼로그로 따로 처리하므로
                애초에 qcTargetYn 대상이 아니다. */}
          {confirmDialog.data?.qcTargetYn === 'Y' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                공정검사
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="생산수량"
                  type="number"
                  size="small"
                  value={completeOutputQty}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setCompleteOutputQty('');
                      return;
                    }
                    const n = parseInt(raw, 10);
                    setCompleteOutputQty(Number.isNaN(n) || n < 0 ? 0 : n);
                  }}
                  error={completeOutputQty === ''}
                  helperText={completeOutputQty === '' ? '생산수량을 입력하세요.' : ' '}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ width: 140 }}
                />
                <TextField
                  label="불량수량"
                  type="number"
                  size="small"
                  value={completeDefectQty}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setCompleteDefectQty(Number.isNaN(n) || n < 0 ? 0 : n);
                  }}
                  error={Number(completeDefectQty) > Number(completeOutputQty || 0)}
                  helperText={Number(completeDefectQty) > Number(completeOutputQty || 0) ? '불량수량이 생산수량을 넘을 수 없습니다.' : ' '}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ width: 140 }}
                />
              </Box>
            </Box>
          )}

          {/* 파일 업로드 영역 */}
          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              bgcolor: completeFiles.length > 0 ? '#f1f8e9' : '#fafafa',
              cursor: 'pointer',
              '&:hover': { borderColor: '#4caf50', bgcolor: '#f1f8e9' },
              transition: 'all 0.2s',
            }}
            onClick={() => document.getElementById('complete-file-input').click()}
          >
            <input
              id="complete-file-input"
              type="file"
              multiple
              hidden
              onChange={(e) => {
                const newFiles = Array.from(e.target.files);
                setCompleteFiles(prev => [...prev, ...newFiles]);
                e.target.value = '';
              }}
            />
            <CloudUploadIcon sx={{ fontSize: 40, color: '#9e9e9e', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              클릭하여 파일을 선택해주세요
            </Typography>
            <Typography variant="caption" color="text.disabled">
              사진, 문서 등 쳊부 가능
            </Typography>
          </Box>

          {/* 선택된 파일 목록 */}
          {completeFiles.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {completeFiles.map((file, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, px: 1, bgcolor: '#f5f5f5', borderRadius: 1, mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    <AttachFileIcon sx={{ fontSize: 18, color: '#666' }} />
                    <Typography variant="body2" noWrap>{file.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({(file.size / 1024).toFixed(0)}KB)
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setCompleteFiles(prev => prev.filter((_, i) => i !== idx))}>
                    <DeleteIcon sx={{ fontSize: 18, color: '#999' }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => {
            setConfirmDialog({ open: false, type: '', data: null });
            setCompleteFiles([]);
            setCompleteRemarks('');
            setCompleteQcYn(false);
            setCompleteOutputQty(0);
            setCompleteDefectQty(0);
          }}>
            취소
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color="success"
            disabled={
              uploadProgress
              || (completeQcYn && completeOutputQty === '')
              || (completeQcYn && Number(completeDefectQty) > Number(completeOutputQty || 0))
            }
            startIcon={uploadProgress ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{ minWidth: 100 }}
          >
            {uploadProgress ? '처리중...' : '완료'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 완품 검사 판정 다이얼로그 (검사 공정 전용 - 합격/불합격) */}
      <Dialog
        open={qcJudgeDialog.open}
        onClose={() => { if (!qcJudgeLoading) closeQcJudgeDialog(); }}
        maxWidth="sm" fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="warning" />
            <span>완품 검사 판정 - {qcJudgeDialog.data?.processNm}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            완품 검사는 부분 불량 없이 전체를 합격/불합격으로 판정합니다.
            <br />
            <strong>불합격 처리하면 이 생산계획은 전량 폐기 처리되며 출하할 수 없습니다.</strong> 되돌릴 수 없는 처리이니 신중히 선택해주세요.
          </Alert>

          <TextField
            label="비고 (선택)"
            value={qcJudgeRemarks}
            onChange={(e) => setQcJudgeRemarks(e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder="판정 사유 등을 입력해주세요"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeQcJudgeDialog} disabled={!!qcJudgeLoading}>
            취소
          </Button>
          <Button
            onClick={() => handleQcJudge('FAIL')}
            variant="contained"
            color="error"
            disabled={!!qcJudgeLoading}
            startIcon={qcJudgeLoading === 'FAIL' ? <CircularProgress size={16} color="inherit" /> : null}
          >
            불합격
          </Button>
          <Button
            onClick={() => handleQcJudge('PASS')}
            variant="contained"
            color="success"
            disabled={!!qcJudgeLoading}
            startIcon={qcJudgeLoading === 'PASS' ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          >
            합격
          </Button>
        </DialogActions>
      </Dialog>

      <ProductionStartDialog
        open={openStartDialog}
        onClose={() => setOpenStartDialog(false)}
        onSave={handleStartProduction}
        planId={planId}
        planData={planData}
        routingData={routingData}
      />

      <ProcessDetailDialog
        open={openProcessDialog}
        onClose={() => { setOpenProcessDialog(false); setSelectedProcess(null); }}
        processData={selectedProcess}
        planData={planData}
        canUpdatePhaseAssignment={canUpdatePhaseAssignment}
        canUpdateClosedPhaseAssignment={canUpdateClosedPhaseAssignment}
        onUpdate={() => Promise.all([fetchRoutingData(), fetchProductionData()])}
      />

      <OutsourceDialog
        open={openOutsourceDialog}
        onClose={() => { setOpenOutsourceDialog(false); setSelectedOutsourceProcess(null); }}
        processData={selectedOutsourceProcess}
        planId={planId}
        outsourceData={
          selectedOutsourceProcess?.processType === 'EXTERNAL' && selectedOutsourceProcess?.outsourceUuid
            ? {
                outsourceUuid: selectedOutsourceProcess.outsourceUuid,
                partnerUuid: selectedOutsourceProcess.partnerUuid,
                partnerNm: selectedOutsourceProcess.partnerNm,
                contactNm: selectedOutsourceProcess.contactNm,
                contactTel: selectedOutsourceProcess.contactTel,
                orderDate: selectedOutsourceProcess.orderDate,
                dueDate: selectedOutsourceProcess.dueDate,
                remarks: selectedOutsourceProcess.remarks,
                status: selectedOutsourceProcess.outsourceStatus,
              }
            : null
        }
        onSaved={() => {
          setOpenOutsourceDialog(false);
          setSelectedOutsourceProcess(null);
          fetchRoutingData();
        }}
      />

      <ProductionDetailDialog
        open={openDetailDialog}
        onClose={() => { setOpenDetailDialog(false); setSelectedProduction(null); }}
        productionData={selectedProduction}
        onUpdate={handleUpdateProduction}
        onStatusChange={handleStatusChange}
        planData={planData}
      />
    </Box>
  );
};

export default ProductionTabContainer;
